import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement, type ReactNode } from "react";
import { Field, Modal, PercentInput, useToast } from "@cx/ui";
import type { TaxRuleView } from "@cx/contracts";
import {
  BRAZIL_UFS,
  CRTS,
  EXTERIOR,
  IE_INDICATORS,
  type Crt,
  type DestUf,
  type IeIndicator,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { CRT_LABEL, IE_INDICATOR_LABEL } from "./tax-labels";

export type RuleValues = Omit<TaxRuleView, "id" | "createdAt" | "updatedAt">;

export const EMPTY_RULE: RuleValues = {
  priority: 0,
  active: true,
  destUf: null,
  destIeIndicator: null,
  destFinalConsumer: null,
  crt: null,
  ncmPrefix: null,
  productId: null,
  productGroupId: null,
  productOrigin: null,
  cfop: "",
  icmsCst: null,
  icmsCsosn: null,
  icmsPct4: null,
  icmsReductionPct4: null,
  icmsCreditSnPct4: null,
  icmsDeferralPct4: null,
  fcpPct4: null,
  icmsStModBc: null,
  icmsStMvaPct4: null,
  icmsStMvaAdjust: false,
  icmsStPct4: null,
  icmsStReductionPct4: null,
  fcpStPct4: null,
  difal: true,
  difalDoubleBase: false,
  icmsDestPct4: null,
  fcpDestPct4: null,
  ipiCst: null,
  ipiPct4: null,
  ipiEnq: null,
  pisCst: null,
  pisPct4: null,
  cofinsCst: null,
  cofinsPct4: null,
  pisCofinsExcludeIcms: true,
  ibsCst: null,
  ibsClass: null,
  ibsUfPct4: null,
  ibsMunPct4: null,
  cbsPct4: null,
  ibsReductionPct4: null,
  cbsReductionPct4: null,
  ibsDeferralPct4: null,
  isPct4: null,
  infoText: null,
};

export const ruleValues = ({ id: _i, createdAt: _c, updatedAt: _u, ...v }: TaxRuleView): RuleValues => v;

type PctKey = { [K in keyof RuleValues]: RuleValues[K] extends number | null ? K : never }[keyof RuleValues];
type TextKey = { [K in keyof RuleValues]: RuleValues[K] extends string | null ? K : never }[keyof RuleValues];
type BoolKey = { [K in keyof RuleValues]: RuleValues[K] extends boolean ? K : never }[keyof RuleValues];

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset className="mb-2">
    <legend className="h6 border-bottom pb-1">{title}</legend>
    <div className="row">{children}</div>
  </fieldset>
);

/**
 * Regra de tributação da natureza. Vazio = padrão do motor (ex.: alíquota interna da UF). A regra mais específica
 * vence; a prioridade desempata (maior primeiro).
 */
export function TaxRuleDialog({
  operationId,
  ruleId,
  initial,
  simples,
  onClose,
}: {
  operationId: string;
  ruleId?: string;
  initial: RuleValues;
  /** Empresa no Simples (CRT 1/4): mostra CSOSN em vez de CST. */
  simples: boolean;
  onClose(): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [v, setV] = useState<RuleValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof RuleValues>(k: K, value: RuleValues[K]) => setV((x) => ({ ...x, [k]: value }));
  const save = useMutation({
    mutationFn: () =>
      api(
        ruleId ? `/tax-operations/${operationId}/rules/${ruleId}` : `/tax-operations/${operationId}/rules`,
        {
          method: ruleId ? "PUT" : "POST",
          body: JSON.stringify(v),
        },
      ),
    onSuccess: () => {
      toast("success", "Regra de tributação salva");
      void qc.invalidateQueries({ queryKey: ["tax-operations", companyId] });
      setOpen(false);
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });
  const pct = (k: PctKey, label: string, hint?: string, cls = "col-md-4") => (
    <Field label={label} className={cls} error={errors[k]} hint={hint}>
      <PercentInput value={v[k] as number | null} onChange={(p) => set(k, p as never)} />
    </Field>
  );
  const txt = (k: TextKey, label: string, maxLength: number, cls = "col-md-4", required = false) => (
    <Field label={label} className={cls} error={errors[k]} required={required}>
      <input
        value={(v[k] as string | null) ?? ""}
        maxLength={maxLength}
        inputMode="numeric"
        onChange={(e) => set(k, (e.target.value.trim() || (k === "cfop" ? "" : null)) as never)}
      />
    </Field>
  );
  const check = (k: BoolKey, label: string) => (
    <div className="col-md-6 mb-2">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`rule-${k}`}
          checked={v[k] as boolean}
          onChange={(e) => set(k, e.target.checked as never)}
        />
        <label className="form-check-label" htmlFor={`rule-${k}`}>
          {label}
        </label>
      </div>
    </div>
  );
  return (
    <Modal
      open={open}
      title={ruleId ? "Editar regra de tributação" : "Nova regra de tributação"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar regra
          </button>
        </>
      }
    >
      <div
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <Group title="Quando aplicar">
          <Field label="UF de destino" className="col-md-4" error={errors.destUf} hint="Vazio = qualquer UF.">
            <select
              className="form-select"
              value={v.destUf ?? ""}
              onChange={(e) => set("destUf", (e.target.value || null) as DestUf | null)}
            >
              <option value="">Qualquer</option>
              {BRAZIL_UFS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
              <option value={EXTERIOR}>Exterior</option>
            </select>
          </Field>
          <Field label="Destinatário" className="col-md-4" error={errors.destIeIndicator}>
            <select
              className="form-select"
              value={v.destIeIndicator ?? ""}
              onChange={(e) =>
                set("destIeIndicator", (e.target.value ? Number(e.target.value) : null) as IeIndicator | null)
              }
            >
              <option value="">Qualquer</option>
              {IE_INDICATORS.map((i) => (
                <option key={i} value={i}>
                  {IE_INDICATOR_LABEL[i]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Consumidor final" className="col-md-4" error={errors.destFinalConsumer}>
            <select
              className="form-select"
              value={v.destFinalConsumer === null ? "" : v.destFinalConsumer ? "1" : "0"}
              onChange={(e) =>
                set("destFinalConsumer", e.target.value === "" ? null : e.target.value === "1")
              }
            >
              <option value="">Qualquer</option>
              <option value="1">Sim</option>
              <option value="0">Não (revenda)</option>
            </select>
          </Field>
          {txt("ncmPrefix", "NCM (início)", 10)}
          <Field label="Origem da mercadoria" className="col-md-4" error={errors.productOrigin}>
            <select
              className="form-select"
              value={v.productOrigin ?? ""}
              onChange={(e) => set("productOrigin", e.target.value === "" ? null : Number(e.target.value))}
            >
              <option value="">Qualquer</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
          <Field label="CRT da empresa" className="col-md-4" error={errors.crt}>
            <select
              className="form-select"
              value={v.crt ?? ""}
              onChange={(e) => set("crt", (e.target.value ? Number(e.target.value) : null) as Crt | null)}
            >
              <option value="">Qualquer</option>
              {CRTS.map((c) => (
                <option key={c} value={c}>
                  {CRT_LABEL[c]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Prioridade"
            className="col-md-4"
            error={errors.priority}
            hint="Desempate: maior vence."
          >
            <input
              type="number"
              min={0}
              max={1000}
              step={1}
              value={v.priority}
              onChange={(e) => set("priority", Math.trunc(Number(e.target.value) || 0))}
            />
          </Field>
        </Group>
        <Group title="ICMS">
          {txt("cfop", "CFOP", 4, "col-md-4", true)}
          {simples ? txt("icmsCsosn", "CSOSN", 3) : txt("icmsCst", "CST", 2)}
          {pct("icmsPct4", "Alíquota interna", "Vazio = alíquota da UF.")}
          {pct("icmsReductionPct4", "Redução da base")}
          {simples ? pct("icmsCreditSnPct4", "Crédito do Simples") : pct("icmsDeferralPct4", "Diferimento")}
          {pct("fcpPct4", "FCP")}
        </Group>
        <Group title="Substituição tributária (ST)">
          <Field label="Base do ST" className="col-md-4" error={errors.icmsStModBc}>
            <select
              className="form-select"
              value={v.icmsStModBc ?? ""}
              onChange={(e) =>
                set("icmsStModBc", (e.target.value ? Number(e.target.value) : null) as 4 | 6 | null)
              }
            >
              <option value="">Sem ST</option>
              <option value="4">Margem de valor agregado (MVA)</option>
              <option value="6">Valor da operação</option>
            </select>
          </Field>
          {pct("icmsStMvaPct4", "MVA")}
          {pct("icmsStPct4", "Alíquota do ST", "Vazio = alíquota interna do destino.")}
          {pct("icmsStReductionPct4", "Redução da base do ST")}
          {pct("fcpStPct4", "FCP ST")}
          {check("icmsStMvaAdjust", "MVA ajustada (interestadual)")}
        </Group>
        <Group title="DIFAL (não contribuinte de outra UF)">
          {check("difal", "Calcular DIFAL")}
          {check("difalDoubleBase", "Base dupla (“por dentro”)")}
          {pct("icmsDestPct4", "Alíquota interna do destino", "Vazio = tabela da UF.")}
          {pct("fcpDestPct4", "FCP do destino")}
        </Group>
        <Group title="IPI">
          {txt("ipiCst", "CST do IPI", 2)}
          {pct("ipiPct4", "Alíquota do IPI")}
          {txt("ipiEnq", "Enquadramento", 3)}
        </Group>
        <Group title="PIS e COFINS">
          {txt("pisCst", "CST do PIS", 2, "col-md-3")}
          {pct("pisPct4", "PIS", "Vazio = pelo regime.", "col-md-3")}
          {txt("cofinsCst", "CST da COFINS", 2, "col-md-3")}
          {pct("cofinsPct4", "COFINS", "Vazio = pelo regime.", "col-md-3")}
          {check("pisCofinsExcludeIcms", "Excluir o ICMS da base (Tema 69)")}
        </Group>
        <Group title="IBS e CBS (reforma tributária)">
          {txt("ibsCst", "CST IBS/CBS", 3)}
          {txt("ibsClass", "cClassTrib", 6)}
          {pct("cbsPct4", "CBS", "Vazio = 0,9% (2026).")}
          {pct("ibsUfPct4", "IBS estadual", "Vazio = 0,1% (2026).")}
          {pct("ibsMunPct4", "IBS municipal")}
          {pct("ibsReductionPct4", "Redução do IBS")}
          {pct("cbsReductionPct4", "Redução da CBS")}
          {pct("ibsDeferralPct4", "Diferimento do IBS")}
          {pct("isPct4", "Imposto seletivo")}
        </Group>
        <div className="row">
          <Field label="Texto para a nota" className="col-12" error={errors.infoText}>
            <textarea
              rows={2}
              maxLength={2000}
              value={v.infoText ?? ""}
              onChange={(e) => set("infoText", e.target.value || null)}
            />
          </Field>
          {check("active", "Regra ativa")}
        </div>
      </div>
    </Modal>
  );
}
